import { Link } from 'react-router-dom';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

const DAYS = [
  { day: 'Monday',    hours: '9:00 AM – 9:00 PM', evening: true  },
  { day: 'Tuesday',   hours: '9:00 AM – 9:00 PM', evening: true  },
  { day: 'Wednesday', hours: '9:00 AM – 9:00 PM', evening: true  },
  { day: 'Thursday',  hours: '9:00 AM – 9:00 PM', evening: true  },
  { day: 'Friday',    hours: '9:00 AM – 9:00 PM', evening: true  },
  { day: 'Saturday',  hours: '9:00 AM – 9:00 PM', evening: true  },
  { day: 'Sunday',    hours: 'Closed',             evening: false, closed: true },
];

const BRING = [
  { icon: 'ri-user-smile-line',           text: 'Yourself — that\'s literally all that\'s required' },
  { icon: 'ri-money-dollar-circle-line',  text: '$30 cash or card to pay at the door' },
  { icon: 'ri-t-shirt-line',              text: 'Comfortable clothes you can move and play in' },
  { icon: 'ri-water-flash-line',          text: 'Water bottle (optional but smart, especially for vocalists)' },
  { icon: 'ri-book-open-line',            text: 'Sheet music you want to work on (totally optional)' },
];

const STARS_NIGHT = [
  { t:'8%',  l:'4%',  s:1.5, d:'0s'   },
  { t:'20%', l:'14%', s:1,   d:'0.8s' },
  { t:'6%',  l:'28%', s:2,   d:'1.5s' },
  { t:'38%', l:'7%',  s:1,   d:'0.4s' },
  { t:'15%', l:'42%', s:1.5, d:'2.1s' },
  { t:'52%', l:'22%', s:1,   d:'1.1s' },
  { t:'12%', l:'55%', s:1,   d:'0.6s' },
  { t:'35%', l:'65%', s:2,   d:'1.8s' },
  { t:'65%', l:'38%', s:1,   d:'0.3s' },
  { t:'22%', l:'78%', s:1.5, d:'1.4s' },
  { t:'48%', l:'88%', s:1,   d:'2.3s' },
  { t:'8%',  l:'92%', s:2,   d:'0.9s' },
  { t:'75%', l:'72%', s:1,   d:'0.2s' },
];

const GOLD_STARS = [
  { t:'25%', l:'18%', d:'1.0s' },
  { t:'60%', l:'52%', d:'0.5s' },
  { t:'32%', l:'82%', d:'1.7s' },
];

export default function WalkInDetails() {
  const { ref: topRef,   isVisible: topVis   } = useScrollAnimation({ threshold: 0.1 });
  const { ref: nightRef, isVisible: nightVis } = useScrollAnimation({ threshold: 0.1 });

  return (
    <section className="py-28 bg-gradient-to-br from-[#FFFBEB]/50 via-white to-[#F5F3FF]/30 relative overflow-hidden">
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-brand-yellow/10 rounded-full blur-3xl pointer-events-none" />

      <div className="relative z-10 max-w-screen-xl mx-auto px-4 sm:px-8">

        {/* Section label */}
        <div className="text-center mb-14">
          <span className="inline-block px-4 py-1.5 rounded-full bg-brand-yellow/15 text-brand-yellow-dark font-body text-xs font-bold uppercase tracking-widest border border-brand-yellow/30 mb-5">
            Know Before You Go
          </span>
          <h2 className="font-heading text-5xl lg:text-6xl font-black text-brand-blue-dark leading-tight">
            Hours, Service Area &amp; What to Have Ready
          </h2>
        </div>

        {/* Hours + What to Bring side by side */}
        <div ref={topRef} className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-10">

          {/* Hours card */}
          <div className={`scroll-hidden slide-left ${topVis ? 'scroll-visible' : ''} rounded-3xl bg-white border border-gray-100 overflow-hidden`}>
            <div className="bg-gradient-to-r from-[#1E3A8A] to-[#5B21B6] px-8 py-6 flex items-center gap-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-white/15 text-white flex-shrink-0">
                <i className="ri-time-line text-lg" />
              </div>
              <div>
                <h3 className="font-heading text-white text-xl font-black">Open Hours</h3>
                <p className="font-body text-white/60 text-xs">Call or message us — we come to you</p>
              </div>
            </div>
            <div className="p-6">
              <div className="flex flex-col gap-2">
                {DAYS.map((d) => (
                  <div
                    key={d.day}
                    className={`flex items-center justify-between px-4 py-3 rounded-xl ${d.closed ? 'opacity-40' : 'bg-[#FFFBEB]/60'}`}
                  >
                    <span className={`font-body text-sm font-semibold ${d.closed ? 'text-gray-400' : 'text-brand-blue-dark'}`}>
                      {d.day}
                    </span>
                    <div className="flex items-center gap-2">
                      {d.evening && !d.closed && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#0F172A] text-brand-yellow font-body text-[10px] font-semibold">
                          <i className="ri-moon-fill text-[9px]" /> Eve
                        </span>
                      )}
                      <span className={`font-body text-sm ${d.closed ? 'text-gray-400' : 'text-gray-600'}`}>
                        {d.hours}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 flex items-center gap-2 px-4 py-3 rounded-xl bg-brand-yellow/10 border border-brand-yellow/25">
                <i className="ri-information-line text-brand-yellow-dark text-sm" />
                <span className="font-body text-brand-yellow-dark text-xs font-medium">
                  Last walk-in accepted at 8:00 PM to ensure a full hour
                </span>
              </div>
            </div>
          </div>

          {/* What to Bring + Location stacked */}
          <div className={`scroll-hidden slide-right ${topVis ? 'scroll-visible' : ''} flex flex-col gap-6`}>
            {/* What to bring */}
            <div className="rounded-3xl bg-white border border-gray-100 p-8 flex-1">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-brand-yellow/15 text-brand-yellow-dark flex-shrink-0">
                  <i className="ri-suitcase-line text-lg" />
                </div>
                <h3 className="font-heading text-brand-blue-dark text-xl font-black">What to Bring</h3>
              </div>
              <ul className="flex flex-col gap-4">
                {BRING.map((item) => (
                  <li key={item.text} className="flex items-center gap-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-xl bg-[#FFFBEB] text-brand-yellow-dark flex-shrink-0">
                      <i className={`${item.icon} text-sm`} />
                    </div>
                    <span className="font-body text-gray-600 text-sm">{item.text}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Location */}
            <div className="rounded-3xl bg-gradient-to-br from-[#1E3A8A] to-[#5B21B6] p-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-white/15 text-white flex-shrink-0">
                  <i className="ri-map-pin-2-line text-lg" />
                </div>
                <h3 className="font-heading text-white text-xl font-black">Find Us</h3>
              </div>
              <p className="font-body text-white/80 text-sm leading-relaxed mb-5">
                <strong className="text-white">The Creative Institute</strong><br />
                Mobile-Based · Fayetteville, NC &amp; Surrounding Areas<br />
                Mon – Sat · 9am – 9pm<br />
                <span className="text-brand-yellow">910-242-4567</span>
              </p>
              <a
                href="tel:9102424567"
                className="whitespace-nowrap inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/15 border border-white/30 text-white font-body font-semibold text-sm cursor-pointer hover:bg-white/25 transition-colors"
              >
                <i className="ri-phone-line text-base" /> Call Ahead
              </a>
            </div>
          </div>
        </div>

        {/* Night Walk-In dark callout */}
        <div
          ref={nightRef}
          className={`scroll-hidden ${nightVis ? 'scroll-visible' : ''} relative rounded-3xl overflow-hidden`}
        >
          {/* Dark night sky */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#020818] via-[#0F172A] to-[#1a1040]" />
          {/* Nebulas */}
          <div className="nebula-1 absolute -top-8 right-10 w-52 h-52 rounded-full pointer-events-none"
            style={{ background: 'radial-gradient(circle, rgba(124,58,237,0.3) 0%, transparent 70%)' }} />
          <div className="nebula-2 absolute bottom-0 left-16 w-44 h-44 rounded-full pointer-events-none"
            style={{ background: 'radial-gradient(circle, rgba(30,58,138,0.35) 0%, transparent 70%)' }} />

          {/* White stars */}
          {STARS_NIGHT.map((star, i) => (
            <div key={i} className="absolute rounded-full bg-white star-twinkle pointer-events-none"
              style={{ top: star.t, left: star.l, width: star.s, height: star.s, animationDelay: star.d }} />
          ))}
          {/* Gold stars */}
          {GOLD_STARS.map((star, i) => (
            <div key={i} className="absolute rounded-full star-twinkle-fast pointer-events-none"
              style={{ top: star.t, left: star.l, width: 1.5, height: 1.5,
                backgroundColor: 'rgba(245,158,11,0.9)', animationDelay: star.d }} />
          ))}
          {/* Shooting star */}
          <div className="shooting-star-1 absolute rounded-full pointer-events-none"
            style={{ top: '22%', right: '12%', height: '1.5px', transformOrigin: 'right center',
              background: 'linear-gradient(to left, rgba(255,255,255,0.9), transparent)',
              transform: 'rotate(-35deg)' }} />

          <div className="relative z-10 px-8 sm:px-14 py-12 flex flex-col lg:flex-row items-center justify-between gap-8">
            <div className="flex items-start gap-5">
              <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-brand-yellow flex-shrink-0">
                <i className="ri-moon-fill text-[#0F172A] text-2xl" />
              </div>
              <div>
                <span className="inline-block px-3 py-1 rounded-full bg-brand-yellow/20 border border-brand-yellow/40 text-brand-yellow font-body text-xs font-semibold uppercase tracking-widest mb-3">
                  Evening Walk-Ins Welcome
                </span>
                <h3 className="font-heading text-white text-3xl font-black leading-tight mb-2">
                  Night Owl? We&apos;ve Got You.<br />
                  <span className="text-brand-yellow">Open Until 9pm, Every Night.</span>
                </h3>
                <p className="font-body text-white/65 text-sm max-w-lg leading-relaxed">
                  Work all day? Walk in after work, after dinner — any time before 8pm last entry. Same $30 rate, same great instruction, same zero-commitment vibe. No alarm clock required.
                </p>
              </div>
            </div>
            <Link
              to="/on-site-programs"
              className="whitespace-nowrap inline-flex items-center gap-2 px-8 py-4 rounded-full bg-brand-yellow text-[#0F172A] font-body font-bold text-sm cursor-pointer hover:bg-yellow-300 transition-all flex-shrink-0"
            >
              <i className="ri-moon-fill text-base" /> View Evening Schedule
            </Link>
          </div>
        </div>

      </div>
    </section>
  );
}
